import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetOrdersQuery,
  useGetOrderStatusMutation,
  useDeleteOrderMutation,
  useMarkOrdersAsReadMutation,
} from "../../redux/reducer/features/Order/orderSlice";
import { OrderStatus } from "./../../models/features/Order/Order";
import PageLoader from "../app/PageLoader";
import { AppState } from "../../redux/reducer/rootReducer";
import DeleteIcon from "@mui/icons-material/Delete";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppGridId } from "../../models/app/AppGridModels";
import {
  formatDate,
  GetAppGridColumns,
} from "../../services/utilities/utility";
import { addDealerDashboardCounts } from "../../redux/reducer/features/Dashboard/DashboardSlice";
import { OrderFilter } from "../../models/app/AppModels";
import ServerAppGrid from "../app/ServerAppGrid";
import {
  customPriceFilterOperators,
  dateFilterOperators,
} from "../app/CustomGridFilterOpereators";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import TruncatedCellWithTooltip from "../common/TruncatedCellWithTooltip";
import ServerAppGridPro from "../app/ServerAppGridPro";
import dayjs from "dayjs";
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface OrderGridProps {}
const OrderGrid: React.FunctionComponent<OrderGridProps> = (props) => {
  const dispatch = useDispatch();
  const stats = useSelector(
    (state: AppState) => state.dashboardReducer.dealerDashboard,
  );
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find((x) => x.gridId === AppGridId.Orders),
  );
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const [tabValue, setTabValue] = useState("orders");
  const [paging, setPaging] = useState<OrderFilter>({
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
    archivedOnly: false,
    dmsSetupId: dmsSetup?.id,
  });
  const [archivedPaging, setArchivedPaging] = useState<OrderFilter>({
    pageIndex: 0,
    pageSize: 10,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
    archivedOnly: true,
    dmsSetupId: dmsSetup?.id,
  });
  const { data, isLoading, isFetching, refetch } = useGetOrdersQuery({
    paging: paging,
  });
  const {
    data: archiveOrdersData,
    isLoading: archiveOrdersLoading,
    isFetching: archiveOrdersFetching,
    refetch: refetchArchiveOrders,
  } = useGetOrdersQuery(
    { paging: archivedPaging },
    { skip: tabValue !== "archiveOrders" },
  );

  const [orders, setOrders] = useState<any[]>([]);
  const [deletedOrders, setDeletedOrders] = useState<any[]>([]);
  const [statusDialog, setStatusDialog] = useState(false);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>();
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [ordersCount, setOrdersCount] = React.useState<number>(0);
  const [archiveOrdersCount, setArchiveOrdersCount] = React.useState<number>(0);

  const [orderStatusMutation, orderStatusMutationResult] =
    useGetOrderStatusMutation();
  const [markAsReadMutation, markAsReadMutationResult] =
    useMarkOrdersAsReadMutation();
  const [deleteOrderMutation, deleteOrderMutationResult] =
    useDeleteOrderMutation();

  const handleClose = () => {
    setDeleteConfirm(false);
    setStatusDialog(false);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleSetOrderStatus = (order: any) => {
    if (order) {
      setOrderStatus({
        saleOrderId: order?.order?.saleOrderId,
        externalOrderNumber: order?.order?.externalOrderNumber,
        orderDate: order?.order?.dateCreated,
        dmsOrderId: order?.everestOrderDetail?.dmsOrderId,
        externalOrderId: order?.order?.externalOrderId,
        sourceType: order?.order?.sourceType,
        dmsError: order?.everestOrderDetail?.dmsError,
      });
    }
  };

  const handleDeleteOrder = (orderId: any) => {
    setDeletedOrders([
      ...deletedOrders,
      orders.find((item) => item?.order.id === orderId),
    ]);
    setOrders(orders.filter((item) => item?.order.id !== orderId));
    setOrdersCount(ordersCount - 1);
    setArchiveOrdersCount(archiveOrdersCount + 1);
    deleteOrderMutation({ orderId: orderId }).then((res) => {});
  };

  useEffect(() => {
    if (data && data.entityList) {
      setOrders(data.entityList);
      setOrdersCount(data.totalRecords);
    }
  }, [data]);

  useEffect(() => {
    if (archiveOrdersData && archiveOrdersData.entityList) {
      setDeletedOrders(archiveOrdersData.entityList);
      setArchiveOrdersCount(archiveOrdersData.totalRecords);
    }
  }, [archiveOrdersData]);

  useEffect(() => {
    if (stats && stats.unreadOrders > 0) {
      markAsReadMutation().then((res: any) => {
        if (res && res.data) {
          dispatch(
            addDealerDashboardCounts({
              unreadOrders: 0,
              unreadQuotes: stats.unreadQuotes,
            }),
          );
        }
      });
    }
  }, []);
  useEffect(() => {
    refetch();
  }, [paging]);

  useEffect(() => {
    refetchArchiveOrders();
  }, [archivedPaging]);
  const columns: GridColDef[] = [
    {
      field: "externalOrderNumber",
      headerName: "Order Id",
      width: 100,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.externalOrderNumber === 0
          ? params.row.order.saleOrderId
          : params.row.order.externalOrderNumber,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <>
            <TruncatedCellWithTooltip value={params.row.order.customerName} />
            {!params.row.order.isCustomerActive && (
              <Tooltip title={"Customer is not active."} placement="top-start">
                <WarningIcon
                  fontSize="medium"
                  color="warning"
                  sx={{
                    marginLeft: 0.5,
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },

    {
      field: "customerEmail",
      headerName: "Email",
      width: 220,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip value={params.row.order.customerEmail} />
        );
      },
    },
    {
      field: "customerAddress",
      headerName: "Address",
      width: 220,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip value={params.row.order.customerAddress} />
        );
      },
    },
    {
      field: "taxAmount",
      headerName: "Tax($)",
      width: 130,
      align: "right",
      headerAlign: "right",
      type: "number",
      filterOperators: customPriceFilterOperators,
      valueGetter: (params: GridRenderCellParams) =>
        parseFloat(params.row.order.taxAmount),
      valueFormatter: (params) => Number(params.value ?? 0).toFixed(2),
    },
    {
      field: "amount",
      headerName: "Amount($)",
      width: 130,
      align: "right",
      type: "number",
      headerAlign: "right",
      filterOperators: customPriceFilterOperators,
      valueGetter: (params: GridRenderCellParams) =>
        params.row.order.amount.toFixed(2),
    },
    {
      field: "dateCreated",
      headerName: "Date",
      type: "date",
      width: 220,
      filterable: true,
      filterOperators: dateFilterOperators,
      valueGetter: (params) =>
        params.row.order?.dateCreated
          ? dayjs.utc(params.row.order.dateCreated).toDate()
          : null,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.order.dateCreated
          ? formatDate(params.row.order.dateCreated)
          : "N/A";
      },
    },
    {
      field: "sourceType",
      headerName: "Source",
      type: "actions",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params.row.order.sourceType}
            size="small"
            color={"success"}
          />
        );
      },
    },
    {
      field: "Order Status",
      headerName: "DMS Status",
      type: "actions",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return getOrderStatusChip(params.row?.everestOrderDetail?.dmsOrderId);
      },
    },
    {
      field: "Action",
      headerName: "Action",
      type: "actions",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <IconButton
              id="basic-button"
              onClick={(n) => {
                handleSetOrderStatus(params.row);
                setStatusDialog(true);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            |
            <IconButton
              aria-label="viewOrder"
              size="small"
              onClick={() => {
                setSelectedOrderId(params.row.order.id);
                setDeleteConfirm(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        );
      },
    },
  ];

  const archiveOrderscolumns: GridColDef[] = [
    {
      field: "externalOrderNumber",
      headerName: "Order Id",
      width: 100,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.order.externalOrderNumber === 0
          ? params.row.order.saleOrderId
          : params.row.order.externalOrderNumber,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      width: 180,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip value={params.row.order.customerName} />
        );
      },
    },

    {
      field: "customerEmail",
      headerName: "Email",
      width: 220,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip value={params.row.order.customerEmail} />
        );
      },
    },
    {
      field: "customerAddress",
      headerName: "Address",
      width: 220,
      editable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TruncatedCellWithTooltip value={params.row.order.customerAddress} />
        );
      },
    },
    {
      field: "taxAmount",
      headerName: "Tax($)",
      width: 130,
      align: "right",
      headerAlign: "right",
      type: "number",
      filterOperators: customPriceFilterOperators,
      valueGetter: (params: GridRenderCellParams) =>
        parseFloat(params.row.order.taxAmount),
      valueFormatter: (params) => Number(params.value ?? 0).toFixed(2),
    },
    {
      field: "amount",
      headerName: "Amount($)",
      width: 130,
      align: "right",
      type: "number",
      headerAlign: "right",
      filterOperators: customPriceFilterOperators,
      valueGetter: (params: GridRenderCellParams) =>
        params.row.order.amount.toFixed(2),
    },
    {
      field: "dateCreated",
      headerName: "Date",
      type: "date",
      width: 220,
      filterable: true,
      filterOperators: dateFilterOperators,
      valueGetter: (params) =>
        params.row.order?.dateCreated
          ? dayjs.utc(params.row.order.dateCreated).toDate()
          : null,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.order.dateCreated
          ? formatDate(params.row.order.dateCreated)
          : "N/A";
      },
    },
    {
      field: "sourceType",
      headerName: "Source",
      type: "actions",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params.row.order.sourceType}
            size="small"
            color={"success"}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      type: "actions",
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip label="Deleted" size="small" color="error" />;
      },
    },
  ];

  return (
    <>
      <Card>
        <TabContext value={tabValue}>
          <TabList
            onChange={handleChange}
            aria-label="lab API tabs example"
            className="custom-tab-list"
          >
            <Tab label={"Orders"} value={"orders"} />
            <Tab label={"Archived"} value={"archiveOrders"} />
          </TabList>
          <TabPanel sx={{ padding: 0 }} value="orders">
            <ServerAppGridPro
              getRowId={(row) => row.order.id}
              gridId={AppGridId.Orders}
              columns={GetAppGridColumns(
                columns,
                gridSetting?.columnVisibilityModel,
              )}
              data={orders}
              isLoading={isLoading || isFetching}
              height={"70vh"}
              paging={paging}
              setPaging={setPaging}
              totalRecords={ordersCount}
              hideCheckBoxes={false}
            ></ServerAppGridPro>
          </TabPanel>
          <TabPanel sx={{ padding: 0 }} value="archiveOrders">
            <ServerAppGridPro
              getRowId={(row) => row.order.id}
              gridId={AppGridId.Archived_Orders}
              columns={archiveOrderscolumns}
              data={deletedOrders}
              isLoading={archiveOrdersLoading || archiveOrdersFetching}
              height={"70vh"}
              paging={archivedPaging}
              setPaging={setArchivedPaging}
              totalRecords={archiveOrdersCount}
              hideCheckBoxes={false}
            ></ServerAppGridPro>
          </TabPanel>
        </TabContext>
      </Card>
      <PageLoader isLoading={orderStatusMutationResult.isLoading}></PageLoader>
      <Dialog
        open={statusDialog}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
        fullWidth
      >
        <DialogTitle align="center">DMS Status</DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          <Table aria-label="customized table">
            <TableBody>
              <StyledTableRow>
                <StyledTableCell>Order Date</StyledTableCell>
                <StyledTableCell>
                  {orderStatus?.orderDate !== null ||
                  orderStatus?.orderDate !== ""
                    ? formatDate(orderStatus?.orderDate)
                    : ""}
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>Order Id</StyledTableCell>
                <StyledTableCell>
                  {orderStatus?.externalOrderNumber === 0
                    ? orderStatus.saleOrderId
                    : orderStatus?.externalOrderNumber}
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>DMS Order Id</StyledTableCell>
                <StyledTableCell>{orderStatus?.dmsOrderId}</StyledTableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>Order Status</StyledTableCell>
                <StyledTableCell>
                  {getOrderStatusChip(orderStatus?.dmsOrderId)}
                </StyledTableCell>
              </StyledTableRow>
              {orderStatus?.sourceType === "Shopify" && (
                <StyledTableRow>
                  <StyledTableCell>Shopify Order</StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      size="small"
                      label="View"
                      color="info"
                      onClick={() => {
                        window.open(
                          `${dmsSetup?.storeUrl}/admin/orders/${orderStatus?.externalOrderId}`,
                          "_blank",
                        );
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
          <Box
            sx={{
              display:
                orderStatus?.dmsOrderId === "-1" && orderStatus.dmsError
                  ? "block"
                  : "none",
            }}
          >
            <Alert severity="error">{orderStatus?.dmsError ?? ""}</Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStatusDialog(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirm}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
        fullWidth
      >
        <DialogTitle align="center">Confirm to Delete</DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          This order will be archived. You will be able to see it in Archive
          Orders tab.
          <br />
          Are you sure to delete this order?
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setDeleteConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            type="submit"
            size="small"
            onClick={() => {
              handleDeleteOrder(selectedOrderId);
              setDeleteConfirm(false);
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderGrid;

const getOrderStatusChip = (status: any) => {
  if (status && status !== "-1")
    return <Chip label="Order created at DMS" color="success" size="small" />;
  else if (status === "-1")
    return <Chip label="Failed" color="error" size="small" />;
  else return <Chip label="Pending" color="warning" size="small" />;
};
